<script setup lang="ts">
import { computed } from 'vue';
import vuetify from '@/plugins/vuetify';
import type { ProformaCurrency, ProformaInfo, ProformaLine, ProformaWindow } from '@/types/ProformaTypes';
import type { ColumnDef } from '@/types/ColumnDef';

const props = defineProps<{
  proformaId: number;
  currencies: ProformaCurrency[];
  userCanEdit: boolean;
  proformaLines: ProformaLine[];
  proformaInfo: ProformaInfo;
  localCurrency: string;
}>();

const emit = defineEmits<{
  (
    e: 'update:proformaWindow',
    payload: {
      proformaLines: ProformaLine[];
      proformaInfo: ProformaInfo;
      currencies: ProformaCurrency[];
    }
  ): void;
}>();

function emitUpdatedProforma(proformaWindow: ProformaWindow) {
  emit('update:proformaWindow', proformaWindow);
}

const exchangeRateColumns = computed(() => {
  const result: ColumnDef<ProformaCurrency>[] = [
    { header: 'USE', field: 'PER_SHOW', align: 'left', width: '50px' },
    { header: 'Currency', field: 'CURRENCY_NAME', dataType: 'string', width: '60px' },
    { header: 'Rate', field: 'RATE', dataType: 'number', align: 'left' },
    { header: 'Sum', field: 'SUM', dataType: 'number', decimals: 2 },
  ];
  return result;
});

function onExchangeRateCheckboxClicked(id: number, value: 0 | 1) {
  const currencies: ProformaCurrency[] = [...props.currencies];
  const currency = currencies.find((c) => c.PER_ID === id);
  if (currency) {
    currency.PER_SHOW = value;
    emitUpdatedProforma({ proformaLines: props.proformaLines, currencies, proformaInfo: props.proformaInfo });
  }
}

function onRateChange(inputValue: string, currency: ProformaCurrency) {
  const currencies: ProformaCurrency[] = [...props.currencies];
  /* const proformaLines = [...props.proformaLines]; */
  let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
  if (Number.isNaN(value) || value === 0) {
    value = undefined;
  }
  if (value !== undefined && value !== currency.RATE) {
    const updatedCurrencies = currencies.map((c) => {
      if (c.PER_ID === currency.PER_ID) {
        return { ...c, RATE: Number(value) };
      }
      return c;
    });
    emitUpdatedProforma({
      proformaLines: props.proformaLines,
      currencies: updatedCurrencies,
      proformaInfo: props.proformaInfo,
    });
  }
}

function getSharedInputProps(label: string, showAsReadonly: boolean = false) {
  let backgroundColor: string | undefined;
  if (showAsReadonly) {
    backgroundColor = vuetify.framework.theme.dark ? 'grey darken-3' : 'grey lighten-4';
  }
  const hideDetails = true;
  const noflex = true;
  const dense = true;
  const disabled = showAsReadonly;
  return {
    backgroundColor,
    hideDetails,
    noflex,
    dense,
    solo: true,
    flat: true,
    outlined: false,
    disabled,
  };
}
</script>
<template>
  <v-card class="px-2 mx-0 grpClosed">
    <v-card-title class="text-body-2 mb-1 pa-0 pt-2 cardTitle align-start"
      >Exchange Rate
      <!-- <div class="ml-auto" >
        <gat-button
          @click="updateCurrencies"
          :disabled="!props.userCanEdit"
          color="primary"
          class="mb-2"
          caption="Update">
        </gat-button></div> -->
    </v-card-title>

    <gat-grid :columns="exchangeRateColumns" :items="props.currencies" class="elevation-0" dense>
      <template #cell="{ column, row, value }">
        <div class="d-flex justify-center" v-if="column.field === 'PER_SHOW'">
          <v-simple-checkbox
            color="primary"
            :value="row.PER_SHOW === 1 ? true : false"
            @input="onExchangeRateCheckboxClicked(row.PER_ID, row.PER_SHOW === 1 ? 0 : 1)"
            :label="value"
            :disabled="!userCanEdit" />
        </div>
        <template v-else-if="column.field === 'CURRENCY_NAME'">
          <gat-edit v-bind="getSharedInputProps(column.header, true)" :value="row.CURRENCY_NAME" data-type="string" />
        </template>
        <template v-else-if="column.field === 'RATE'">
          <gat-edit
            v-bind="getSharedInputProps(column.header)"
            :value="row.RATE"
            @blur="($event) => [onRateChange($event.target.value, row)]"
            :readonly="!userCanEdit"
            data-type="number" />
        </template>
        <template v-else-if="column.field === 'SUM'">
          <gat-edit
            v-bind="getSharedInputProps(column.header, true)"
            :value="row.PER_SHOW === 1 ? row.SUM : undefined"
            data-type="number"
            reverse
            :decimals="2" />
        </template>
      </template>
    </gat-grid>
  </v-card>
</template>

<style lang="scss" scoped>
::v-deep
  .v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:not(:last-child)
  > td:not(.v-data-table__mobile-row),
.theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:not(:last-child)
  > th:not(.v-data-table__mobile-row) {
  border: thin solid #e0e0e0 !important;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  padding: 0px !important;
}

::v-deep
  .theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  background: none !important;
}
// Set the text color of disabled inputs.
::v-deep .theme--light.v-input--is-disabled > .v-input__control > .v-input__slot > .v-text-field__slot > input {
  color: rgba(0, 0, 0, 0.7) !important;
}

::v-deep .theme--dark.v-input--is-disabled > .v-input__control > .v-input__slot > .v-text-field__slot > input {
  color: rgba(255, 255, 255, 0.7) !important;
}
::v-deep .v-simple-checkbox > .v-input--selection-controls__input {
  margin-right: 0px !important;
}

.grpClosed {
  margin-top: 1px;
  margin-bottom: 1px;
}
.cardTitle {
  color: var(--gatGroupCaption);
}
</style>
